import React, { useEffect, useMemo, useState } from "react";
import { useGroupChatContext } from "../../../context/groupchatContext";
import SettingsSideBar from "./SettingsSideBar";
import SettingsContent from "./SettingsContent";
import { getVisibleSettingsTabs, settings_sidebar_tabs } from "./settings_constants";
import { useGetNurseLocationsQuery } from "../../../services/location_services";
import { locationsParams } from "./SettingsContent/Locations/locations_constants";

function SettingsPage() {
  const groupChatContext = useGroupChatContext();

  const { setShowSettings, authData, userAcl } = useMemo(() => ({
    setShowSettings: groupChatContext.setShowSettings,
    authData: groupChatContext.authData,
    userAcl: groupChatContext.userAcl,
  }));

  const [activeTab, setActiveTab] = useState(
    authData?.is_provider ? "report_a_problem" : "user_profile"
  );

  const nurseLocationAccessParams = useMemo(() => ({
    ...locationsParams,
    page: 1,
    start: 0,
    limit: 1,
  }), []);

  const {
    data: nurseLocationsData,
    isLoading: isNurseLocationsLoading,
    isFetching: isNurseLocationsFetching,
  } = useGetNurseLocationsQuery(nurseLocationAccessParams, {
    skip: !!authData?.is_provider || !authData?.is_facility_admin,
  });

  const visibleTabs = useMemo(
    () => getVisibleSettingsTabs(settings_sidebar_tabs, {
      authData,
      userAcl,
      nurseLocationsData,
      isNurseLocationsLoading: isNurseLocationsLoading || isNurseLocationsFetching,
    }),
    [authData, userAcl, nurseLocationsData, isNurseLocationsLoading, isNurseLocationsFetching]
  );

  useEffect(() => {
    if (!visibleTabs.length) {
      setActiveTab("");
      return;
    }
    const activeTabAvailable = visibleTabs.some((tab) => tab?.slug === activeTab);
    if (!activeTabAvailable) {
      setActiveTab(visibleTabs[0]?.slug || "");
    }
  }, [activeTab, visibleTabs]);

  const handleGoBack = () => {
    setShowSettings(false);
  };

  const commonProps = {
    activeTab,
    setActiveTab,
    tabs: visibleTabs,
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-gray-50/50">
      {/* Header */}
      <header
        className={`flex items-center justify-between shrink-0 ${
          authData?.is_provider ? "bg-ethizo_primary" : "bg-bg-primary"
        } flex-row px-4 sm:px-6 py-3 sm:py-4 h-14 sm:h-16 text-white shadow-sm`}
      >
        <h1 className="text-lg sm:text-xl font-semibold truncate">Settings</h1>
        <button
          onClick={handleGoBack}
          title="Go Back to Home"
          aria-label="Go Back to Home"
          className="font-medium py-2 px-3 sm:px-4 rounded-lg hover:bg-white/15 active:scale-[0.98] transition-all text-sm sm:text-base shrink-0"
        >
          Back to Home
        </button>
      </header>

      <div className="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
        {/* Mobile: horizontal tabs above content */}
        <div className="md:hidden shrink-0">
          <SettingsSideBar {...commonProps} variant="horizontal" />
        </div>

        {/* Desktop: sidebar */}
        <aside className="hidden md:block w-48 lg:w-56 xl:w-60 shrink-0 overflow-y-auto border-r border-gray-200 bg-white">
          <SettingsSideBar {...commonProps} variant="vertical" />
        </aside>

        {/* Content area */}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-gray-50/50">
          <SettingsContent {...commonProps} />
        </main>
      </div>
    </div>
  );
}

export default SettingsPage;
